import { TDocumentDefinitions } from "pdfmake/interfaces";

import CompanyLogoForm from "react-lib/apps/HISV3/common/CompanyLogoForm";
import { meHealthDivision, OADetailType } from "react-lib/apps/HISV3/common/HeaderPdfFormTemplate";
import { formatISOtoBEDateTime } from "react-lib/utils/dateUtils";

export type FormPatientQueueProps = {
  oaDetail?: OADetailType;
  startDate?: string;
  endDate?: string;
  encounterData?: any[];
  printedBy?: string;
  printDate?: string;
  printTime?: string;
  division?: string;
  divisionCode?: string;
};

const COLOR = {
  black: "#000000",
  bannerBg: "#F9C1D0",
};

const FormPatientQueue = async (props: FormPatientQueueProps): Promise<TDocumentDefinitions> => {
  const { font, images, logoWidth } = await CompanyLogoForm({
    font: "THSarabunNew",
    form: "FormPatientQueue",
    height: 50,
    logoImage: props.oaDetail?.imageFile,
  });

  const fields = props.encounterData ?? [];

  const fileData: any[][] = fields
    .sort((a, b) => (a.service_datetime < b.service_datetime ? -1 : 1))
    .map((item, index) => {
      const divisionHN = (item?.hn || "").replace(`${props?.divisionCode || ""}_`, "");
      const patientAge = (item?.patient_age || "").split(" ")[0];
      const queueDate = `${formatISOtoBEDateTime(item?.service_datetime)?.date} (${formatISOtoBEDateTime(item?.service_datetime)?.timeShort})`;

      const packageNames = item.package_names || [];
      const packageLable =
        packageNames.length > 1
          ? {
              stack: packageNames.map((data) => ({
                text: `• ${data}`,
              })),
            }
          : { text: packageNames[0] ?? "-", alignment: "left", marginLeft: 10 };

      return [
        {
          text: index + 1 || "",
          fontSize: 12,
          alignment: "center",
        },
        {
          text: `${divisionHN} (${item?.number || ""})` || "",
          fontSize: 12,
          alignment: "center",
        },
        {
          text: item?.patient_name || "",
          fontSize: 12,
        },
        {
          text: patientAge || "",
          fontSize: 12,
          alignment: "center",
        },
        {
          ...packageLable,
          fontSize: 12,
          alignment: "left",
          marginLeft: 10,
        },
        {
          text: queueDate || "",
          fontSize: 12,

          alignment: "center",
        },
        {
          text: item?.patient_phone_number,
          fontSize: 12,
          alignment: "center",
        },
        {
          text: item?.remark || "",
          fontSize: 12,
        },
      ];
    });

  return {
    defaultStyle: { font, lineHeight: 1.1 },
    images: { ...images },
    pageMargins: [20, 115, 20, 65],
    pageSize: "A4",
    pageOrientation: "landscape",
    header: () => ({
      margin: [20, 12, 20, 0],
      stack: [
        {
          columns: [
            props.oaDetail?.imageFile
              ? { image: "logo", width: logoWidth, height: 50, marginLeft: 15 }
              : { text: "", width: 55 },
            { text: "", width: 15 },
            {
              stack: meHealthDivision.includes(props.division || "")
                ? [
                    {
                      text: "มีเฮลท์ คลินิกเทคนิคการแพทย์",
                      bold: true,
                      fontSize: 16,
                      lineHeight: 1,
                    },
                    {
                      text: "261/27-28 ซอยจรัญสนิทวงศ์ 29/1 ถนนจรัญสนิทวงศ์ แขวงบางขุนศรี เขตบางกอกน้อย กรุงเทพฯ 10700",
                      fontSize: 12,
                      lineHeight: 1,
                    },
                    {
                      text: "โทร . 083-838-4657   อีเมล : mehealth@morishealth.com",
                      fontSize: 12,
                      lineHeight: 1,
                    },
                  ]
                : [
                    {
                      text: props.oaDetail?.name,
                      bold: true,
                      fontSize: 16,
                      lineHeight: 1,
                    },
                    {
                      text: props.oaDetail?.addressTH,
                      fontSize: 12,
                      lineHeight: 1,
                    },
                    {
                      text: "",
                      fontSize: 12,
                      lineHeight: 1,
                    },
                  ],
              width: "*",
              alignment: "left",
              marginLeft: 15,
            },
          ],
        },
        {
          table: {
            widths: ["*"],
            body: [
              [
                {
                  text: `รายงานผู้รับบริการ วันที่ ${props.startDate} ถึง ${props.endDate}`,
                  bold: true,
                  fontSize: 18,
                  alignment: "center",
                  fillColor: COLOR.bannerBg,
                  margin: [0, 4, 0, 1],
                },
              ],
            ],
          },
          layout: { hLineWidth: () => 0, vLineWidth: () => 0 },
          marginTop: 10,
          marginBottom: 3,
        },
      ],
    }),
    content: [
      {
        stack: [
          {
            table: {
              headerRows: 1,
              dontBreakRows: true,
              widths: ["6%", "11%", "23%", "4%", "20%", "12%", "10%", "*"],
              body: [
                [
                  {
                    text: "ลำด้บ",
                    bold: true,
                    fontSize: 13,
                    alignment: "center",
                    marginTop: 10,
                  },
                  {
                    text: `MemberID\n(หมายเลขรับบริการ)`,
                    bold: true,
                    fontSize: 13,
                    alignment: "center",
                  },
                  {
                    text: "ชื่อ-นามสกุล",
                    bold: true,
                    fontSize: 13,
                    alignment: "center",
                    marginTop: 10,
                  },
                  {
                    text: "อายุ",
                    bold: true,
                    fontSize: 13,
                    alignment: "center",
                    marginTop: 10,
                  },
                  {
                    text: `Package /\n(รายการตรวจ)`,
                    bold: true,
                    fontSize: 13,
                    alignment: "center",
                  },
                  {
                    text: "วันเวลาที่รับบริการ",
                    bold: true,
                    fontSize: 13,
                    alignment: "center",
                    marginTop: 10,
                  },
                  {
                    text: "เบอร์โทรศัพท์",
                    bold: true,
                    fontSize: 13,
                    alignment: "center",
                    marginTop: 10,
                  },
                  {
                    text: "หมายเหตุ",
                    bold: true,
                    fontSize: 13,
                    alignment: "center",
                    marginTop: 10,
                  },
                ],
                ...fileData,
              ],
            },
          },
        ],
      },
    ],
    footer: function (currentPage: number, pageCount: number) {
      return {
        stack: [
          {
            width: "*",
            alignment: "left",
            text: [
              { text: "ผู้พิมพ์ (Printed by): ", fontSize: 12, bold: true },
              { text: `${props.printedBy}`, fontSize: 12 },
            ],
          },
          {
            columns: [
              {
                width: "*",
                alignment: "left",
                text: [
                  { text: "วัน เวลา ที่พิมพ์ (Printed Date/Time): ", fontSize: 12, bold: true },
                  { text: `${props.printDate} (${props.printTime})`, fontSize: 12 },
                ],
              },
              {
                width: "auto",
                alignment: "right",
                text: [
                  { text: "หน้าที่ (Page): ", fontSize: 12, bold: true },
                  { text: `${currentPage}/${pageCount}`, fontSize: 12 },
                ],
              },
            ],
          },
        ],
        margin: [20, 15, 20, 0],
      };
    },
  };
};

export default FormPatientQueue;
